import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import {
  PlusIcon,
  FunnelIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import productAnalyticsService, { type Funnel } from '../../services/api/product-analytics.service';
import FunnelBuilderModal, { FunnelFormData } from '../../components/analytics/FunnelBuilderModal';
import { usePermissions } from '../../hooks/usePermissions';

export default function FunnelsPage() {
  const { t } = useTranslation('analytics');
  const { canCreateFunnels, canEditFunnels, canDeleteFunnels } = usePermissions();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState<Funnel | null>(null);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load funnels when app changes
  useEffect(() => {
    loadFunnels();
  }, [selectedAppId]);

  const loadFunnels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productAnalyticsService.listFunnels(selectedAppId || 'all');
      setFunnels(data || []);
    } catch (err: any) {
      console.error('Error loading funnels:', err);
      setError(err.message || 'Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (funnel: Funnel) => {
    if (!selectedAppId) {
      toast.error(t('funnels.selectApplicationRequired') || 'Please select an application', {
        duration: 4000,
        icon: '⚠️',
      });
      return;
    }
    setSelectedFunnel(funnel);
    setShowEditModal(true);
  };

  const handleDelete = (funnel: Funnel) => {
    setSelectedFunnel(funnel);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedFunnel) return;

    try {
      await productAnalyticsService.deleteFunnel(selectedFunnel.id);
      setFunnels(funnels.filter(f => f.id !== selectedFunnel.id));
      setShowDeleteModal(false);
      setSelectedFunnel(null);
    } catch (err: any) {
      console.error('Error deleting funnel:', err);
      toast.error('Failed to delete funnel: ' + (err.message || 'Unknown error'), {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleSaveFunnel = async (formData: FunnelFormData) => {
    // Convert FunnelFormData to CreateFunnelRequest
    const requestData = {
      name: formData.name,
      description: formData.description,
      applicationId: selectedAppId,
      steps: formData.steps.map(step => ({
        stepOrder: step.stepOrder,
        eventName: step.eventName,
        eventFilters: step.eventFilters,
      })),
    };

    if (selectedFunnel) {
      // Update existing funnel
      const updated = await productAnalyticsService.updateFunnel(selectedFunnel.id, requestData);
      setFunnels(funnels.map(f => f.id === updated.id ? updated : f));
    } else {
      // Create new funnel
      const created = await productAnalyticsService.createFunnel(requestData);
      setFunnels([...funnels, created]);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getConversionColor = (conversion: number): string => {
    if (conversion >= 50) return 'text-green-600';
    if (conversion >= 30) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('funnels.title')}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('funnels.subtitle')}
            </p>
          </div>
          {canCreateFunnels && (
            <button
              onClick={() => {
                if (!selectedAppId) {
                  toast.error(t('funnels.selectApplicationRequired') || 'Please select an application', {
                    duration: 4000,
                    icon: '⚠️',
                  });
                  return;
                }
                setShowCreateModal(true);
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="w-5 h-5" />
              <span>{t('funnels.create')}</span>
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={loadFunnels}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : funnels.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12">
            <div className="text-center">
              <FunnelIcon className="mx-auto w-16 h-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No Funnels Found
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Get started by creating your first conversion funnel.
              </p>
              <button
                onClick={() => {
                  if (!selectedAppId) {
                    toast.error(t('funnels.selectApplicationRequired'), {
                      duration: 3000,
                      icon: '⚠️',
                    });
                    return;
                  }
                  setShowCreateModal(true);
                }}
                className="mt-6 flex items-center space-x-2 mx-auto px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all"
              >
                <PlusIcon className="w-5 h-5" />
                <span>{t('funnels.create')}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {funnels.map((funnel) => (
              <div
                key={funnel.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                {/* Funnel Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                      <FunnelIcon className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {funnel.name}
                      </h3>
                      {funnel.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {funnel.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {(canEditFunnels || canDeleteFunnels) && (
                    <div className="flex items-center space-x-2">
                      {canEditFunnels && (
                        <button
                          onClick={() => handleEdit(funnel)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Edit funnel"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                      {canDeleteFunnels && (
                        <button
                          onClick={() => handleDelete(funnel)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Delete funnel"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Funnel Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Steps</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {funnel.step_count}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Events</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {funnel.steps?.length || funnel.step_count}
                    </p>
                  </div>
                </div>

                {/* Funnel Visualization (mini) */}
                <div className="mb-4 overflow-hidden">
                  <div className="flex items-center justify-center space-x-1">
                    {Array.from({ length: funnel.step_count }).map((_, index) => {
                      // Calculate width that stays within bounds
                      const baseWidth = 100 / funnel.step_count;
                      const reduction = index * (40 / funnel.step_count);
                      const width = Math.max(baseWidth - reduction, 15);
                      return (
                        <div
                          key={index}
                          className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded transition-all duration-300 hover:scale-105"
                          style={{
                            width: `${width}%`,
                            height: '28px',
                            opacity: 1 - (index * 0.12),
                            maxWidth: '100%',
                          }}
                          title={`Step ${index + 1}`}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Funnel Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Created: {new Date(funnel.created_at).toLocaleDateString()}
                  </span>
                  <Link
                    to={`/analytics/funnels/${funnel.id}`}
                    className="flex items-center space-x-1 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    <ChartBarIcon className="w-4 h-4" />
                    <span>View Analysis</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Funnel Modal */}
        {(showCreateModal || showEditModal) && selectedAppId && (
          <FunnelBuilderModal
            applicationId={selectedAppId}
            onClose={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              setSelectedFunnel(null);
            }}
            onSave={handleSaveFunnel}
            initialData={selectedFunnel ? {
              name: selectedFunnel.name,
              description: selectedFunnel.description || '',
              steps: selectedFunnel.steps?.map(step => ({
                id: step.id,
                stepOrder: step.step_order,
                eventName: step.event_name,
                eventFilters: step.event_filters || {
                  id: 'root',
                  logicOperator: 'AND',
                  conditions: [],
                  groups: [],
                },
              })) || [],
            } : undefined}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedFunnel && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 mx-auto mb-4">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                  Delete Funnel
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                  Are you sure you want to delete "{selectedFunnel.name}"? This action cannot be undone.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedFunnel(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
